import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Barcode, BarcodeFormat, LensFacing, StartScanOptions } from '@capacitor-mlkit/barcode-scanning';
import { InputCustomEvent, ModalController, Platform } from '@ionic/angular';
import { fromEvent, Subject, takeUntil } from 'rxjs';
import { BarcodeScanningService } from 'src/app/shared/components/document/document-edit/nodes/barcode/barcode-scanning.service';

// This dialogue is copied from the capacitor barcode example project and modified to fit our needs
// The functionality for flashlight, zoom and scan area (square) is all inside this component
// We can change design, layout, etc. if needed.
// https://github.com/robingenz/capacitor-mlkit-plugin-demo/tree/main

import { ExtendedBarcode } from 'src/app/shared/components/document/document-edit/nodes/barcode/barcode-scanning.service';
@Component({
  selector: 'app-barcode-scanning',
  template: ` <ion-header>
      <ion-toolbar>
        <ion-title>Barcodescanner</ion-title>

        <ion-buttons slot="end">
          <ion-button (click)="closeModal()">
            <ion-icon name="close"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <div #square class="square" [ngClass]="{ 'barcode-found': this.barcodesInSquare.length > 0 }"></div>
      <div class="barcode-text">{{ barcodeText }}</div>
      <div class="zoom-ratio-wrapper">
        <ion-range
          [min]="minZoomRatio"
          [max]="maxZoomRatio"
          [disabled]="minZoomRatio === undefined || maxZoomRatio === undefined"
          (ionChange)="setZoomRatio($any($event))"
        ></ion-range>
      </div>
      <div class="accept-fab-wrapper">
        <ion-fab slot="fixed" horizontal="end" vertical="bottom">
          <ion-fab-button (click)="closeModal(this.barcodesInSquare)" [disabled]="this.barcodesInSquare.length === 0">
            <ion-icon name="alberta-save"></ion-icon>
          </ion-fab-button>
        </ion-fab>
      </div>
      <div class="flashlight-fab-wrapper" *ngIf="isTorchAvailable">
        <ion-fab slot="fixed" horizontal="end" vertical="bottom">
          <ion-fab-button (click)="toggleTorch()">
            <ion-icon name="flashlight"></ion-icon>
          </ion-fab-button>
        </ion-fab>
      </div>
    </ion-content>`,
  styles: [
    `
      ion-content {
        --background: transparent;
      }

      .barcode-text {
        position: absolute;
        top: calc(50% + 170px); /* just below the square */
        left: 50%;
        transform: translateX(-50%);
        color: white;
        font-weight: bold;
        font-size: 1.2em;
        text-align: center;
        max-width: 90%;
      }

      .square {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        border-radius: 16px;
        width: calc(100% - 30px);
        height: calc(100% - 30px);
        max-height: 300px;
        border: 6px solid white;
        box-shadow: 0 0 0 4000px rgba(0, 0, 0, 0.3);
      }

      .barcode-found {
        border: 6px solid green;
      }

      .zoom-ratio-wrapper {
        position: absolute;
        left: 50%;
        bottom: 26px;
        transform: translateX(-50%);
        width: 50%;
        flex-grow: 1;
      }

      .accept-fab-wrapper {
        margin-left: calc(50% + 50px);
        ion-fab {
          right: calc(10px + var(--ion-safe-area-right, 0px));
          bottom: 20px;
        }
      }

      .flashlight-fab-wrapper {
        margin-left: calc(50% + 50px);
        ion-fab {
          left: calc(10px + var(--ion-safe-area-right, 0px));
          bottom: 20px;
        }
      }
    `,
  ],
  providers: [BarcodeScanningService],
})
export class BarcodeScanningModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  public formats: BarcodeFormat[] = [];
  @Input()
  public lensFacing: LensFacing = LensFacing.Back;
  @Input()
  public parseGS1 = false;

  @ViewChild('square')
  public squareElement: ElementRef<HTMLDivElement> | undefined;
  public isTorchAvailable = false;
  public minZoomRatio: number | undefined = 0;
  public maxZoomRatio: number | undefined = 1;
  public scanBusy = false;
  public barcodeText: string | undefined;
  private _barcodeListener: any;
  private _scaledRect: DOMRect | undefined;
  private _ngUnsubscribe = new Subject<void>();
  private _options: StartScanOptions = { formats: this.formats, lensFacing: this.lensFacing };
  public barcodesInSquare: ExtendedBarcode[] = [];

  constructor(
    private _modalController: ModalController,
    private _platform: Platform,
    private _barcodeScanningService: BarcodeScanningService
  ) {}

  public async ngOnInit() {
    this.isTorchAvailable = await this._barcodeScanningService.isTorchAvailable();
  }

  public ngAfterViewInit(): void {
    // Hide everything to make the camera visible
    // Workaround as in barcode scanner documentation
    document.querySelector('body')?.classList.add('barcode-scanning-active');
    setTimeout(() => {
      this.startScan();
    }, 100);

    this._platform.ready().then(() => {
      fromEvent(window, 'orientationchange')
        .pipe(takeUntil(this._ngUnsubscribe))
        .subscribe(this.onOrientationChange.bind(this));
    });
  }
  // Workaround for broken orientation change on ipad10
  @HostListener('window:orientationchange', ['$event'])
  async onOrientationChange(event: Event): Promise<void> {
    await this._barcodeScanningService.stopScan();
    this._barcodeListener.remove();
    await this.startScan();
  }

  public async ngOnDestroy(): Promise<void> {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
    if (this._barcodeListener) {
      this._barcodeListener.remove();
    }
    await this.stopScan();
  }

  public setZoomRatio(event: InputCustomEvent): void {
    if (!event.detail.value) {
      return;
    }
    const zoomLevel = parseInt(event.detail.value as any, 10);
    this._barcodeScanningService.setZoomRatio(zoomLevel);
  }

  public async closeModal(barcodes?: ExtendedBarcode[]): Promise<void> {
    await this.stopScan();
    await this._modalController.dismiss({
      barcode: barcodes?.[0], // added for backwards compatibility
      barcodes,
    });
  }

  public async toggleTorch(): Promise<void> {
    await this._barcodeScanningService.toggleTorch();
  }

  private async addBarcodeListener(): Promise<void> {
    this._barcodeListener = await this._barcodeScanningService.addBarcodeListener(async event => {
      if (this.scanBusy) {
        return;
      }

      this.scanBusy = true;
      this.barcodeText = undefined;

      await this.processBarcodes(event.barcodes);

      this.scanBusy = false;
      this._barcodeScanningService.trackBarcodeReceived();

      if (this.barcodesInSquare.length > 0) {
        this.barcodeText = this._barcodeScanningService.processBarcodeText(this.barcodesInSquare[0], this.parseGS1);
      } else {
        this.barcodeText = undefined;
      }
    });
  }

  private async processBarcodes(barcodes: Barcode[]): Promise<void> {
    this._scaledRect = await this.calculateSquareElement();
    if (!this._scaledRect) {
      return;
    }

    const detectionCornerPoints = this.getSquareElementCornerPoints();
    if (!detectionCornerPoints) {
      return;
    }

    this.barcodesInSquare = this._barcodeScanningService.filterBarcodesInSquare(barcodes, detectionCornerPoints);
  }

  private async startScan(): Promise<void> {
    await this.addBarcodeListener();
    await this._barcodeScanningService.startScan(this._options);
    this.minZoomRatio = await this._barcodeScanningService.getMinZoomRatio();
    this.maxZoomRatio = await this._barcodeScanningService.getMaxZoomRatio();
  }

  private getSquareElementCornerPoints(): number[][] | undefined {
    if (!this._scaledRect) {
      return undefined;
    }
    return [
      [this._scaledRect.left, this._scaledRect.top],
      [this._scaledRect.left + this._scaledRect.width, this._scaledRect.top],
      [this._scaledRect.left + this._scaledRect.width, this._scaledRect.top + this._scaledRect.height],
      [this._scaledRect.left, this._scaledRect.top + this._scaledRect.height],
    ];
  }

  /**
   * Calculates the size of the square element in which the barcode has to be placed to be valid
   */
  private async calculateSquareElement(): Promise<any> {
    let retryCount = 0;
    const maxRetries = 50;
    const retryDelayInMs = 200;
    let scaledRect;

    // We dont know exactly when the square element is rendered, so we have to retry until we get the position and size
    while (retryCount < maxRetries) {
      const squareElementBoundingClientRect = this.squareElement?.nativeElement.getBoundingClientRect();
      scaledRect = squareElementBoundingClientRect
        ? {
            left: squareElementBoundingClientRect.left * window.devicePixelRatio,
            right: squareElementBoundingClientRect.right * window.devicePixelRatio,
            top: squareElementBoundingClientRect.top * window.devicePixelRatio,
            bottom: squareElementBoundingClientRect.bottom * window.devicePixelRatio,
            width: squareElementBoundingClientRect.width * window.devicePixelRatio,
            height: squareElementBoundingClientRect.height * window.devicePixelRatio,
          }
        : undefined;

      // If the square element is not rendered yet, all values of scaledRect will be 0
      // So we wait and try again
      if (scaledRect && Object.values(scaledRect).every(value => value === 0)) {
        retryCount++;
        await new Promise(resolve => setTimeout(resolve, retryDelayInMs));
      } else {
        // Exit the loop if scaledRect is valid
        return scaledRect;
      }
    }
    if (retryCount === maxRetries) {
      try {
        throw new Error('The square element is not rendered after max retries');
      } catch (error) {
        window.logger.error('Scanner Error:', error);
      }
      return undefined;
    }
  }

  private async stopScan(): Promise<void> {
    // Show everything behind the modal again
    document.querySelector('body')?.classList.remove('barcode-scanning-active');
    await this._barcodeScanningService.stopScan();
  }
}
